<script setup>
defineOptions({
  name: 'GameProviders'
});

import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useProvidersStore } from '../stores/providers/providersStore';
import { useGamesStore } from '../stores/games/gamesStore';
import config from '../config'; 
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const providersStore = useProvidersStore();
const gamesStore = useGamesStore();

// Contenedor de scroll
const scrollContainer = ref(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);


const scrollLeft = () => {
  scrollContainer.value.scrollBy({ left: -200, behavior: 'smooth' });
  setTimeout(updateScrollButtons, 300);
};

const scrollRight = () => {
  scrollContainer.value.scrollBy({ left: 200, behavior: 'smooth' });
  setTimeout(updateScrollButtons, 300);
};

const updateScrollButtons = () => {
  const el = scrollContainer.value;
  if (!el) return;

  const tolerance = 5;
  canScrollLeft.value = el.scrollLeft > tolerance;
  canScrollRight.value = Math.ceil(el.scrollLeft + el.clientWidth + tolerance) < el.scrollWidth;

  // Centrado dinámico solo si no hay scroll
  if (el.scrollWidth <= el.clientWidth + tolerance) {
    el.classList.add('no-scroll');
  } else {
    el.classList.remove('no-scroll');
  }
};




// Detectar dispositivo móvil
const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};



// Función para corregir las URLs de las imágenes
const getCorrectImageUrl = (url) => {
  if (!url) return null;
  
  // Si la URL ya es absoluta (comienza con http:// o https://), la dejamos igual
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Si es una ruta relativa, la combinamos con storageUrl
  // Quitamos el /storage inicial si existe, ya que storageUrl ya lo incluye
  const path = url.startsWith('/storage/') ? url.substring(9) : url.startsWith('/') ? url.substring(1) : url;
  return `${config.storageUrl}/${path}`;
};

// Para el dropdown personalizado
// Para el dropdown personalizado
const isDropdownOpen = ref(false);

// Adaptar para usar handleProviderChange existente
const handleCustomDropdownChange = (value) => {
  // Crear un objeto similar al evento para mantener compatibilidad
  const mockEvent = {
    target: {
      value: value
    }
  };
  
  // Usar la función existente
  handleProviderChange(mockEvent);
  
  // Cerrar el dropdown
  isDropdownOpen.value = false;
};

// Función para manejar clics fuera del dropdown
const handleClickOutside = (event) => {
  const dropdown = document.querySelector('.custom-dropdown');
  if (dropdown && !dropdown.contains(event.target)) {
    isDropdownOpen.value = false;
  }
};

onMounted(() => {
  // Detectar si es móvil
  checkMobile();
  window.addEventListener('resize', checkMobile);

  // Esperar a que renderice para calcular bien el scroll
  setTimeout(() => {
    updateScrollButtons();

    // Detectar scroll para volver a calcular posición de flechas
    if (scrollContainer.value) {
      scrollContainer.value.addEventListener('scroll', updateScrollButtons);
    }
  }, 100); // Espera pequeña para asegurar layout completo

  // Detectar clic fuera del dropdown
  document.addEventListener('click', handleClickOutside);
});


onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile);

  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', updateScrollButtons);
  }

  document.removeEventListener('click', handleClickOutside);
});


// Props para configuración
const props = defineProps({
  // Modo de visualización (rows o dropdown)
  displayMode: {
    type: String,
    default: 'dropdown',  // 'rows' o 'dropdown'
    validator: (value) => ['rows', 'dropdown'].includes(value)
  },
  // Categoría para filtrar proveedores (si es null, muestra todos)
  category: {
    type: String,
    default: null
  },
  // Número máximo de proveedores por fila cuando se muestra en modo fila
  providersPerRow: {
    type: Number,
    default: 10
  },
  // Mostrar opción "all" 
  showAllOption: {
    type: Boolean,
    default: true
  }
});

// Computed properties
const selectedProvider = computed({
  get: () => providersStore.selectedProvider,
  set: (value) => providersStore.setSelectedProvider(value)
});

// Filtrar proveedores según la categoría
// Función auxiliar para agrupar proveedores duplicados dinámicamente
const groupDuplicateProviders = (providers) => {
  // Clonar array para no modificar el original
  const result = [];
  
  // Mapa para seguir los códigos que ya hemos procesado (insensible a mayúsculas/minúsculas)
  const processedCodes = {};
  
  // Para cada proveedor
  providers.forEach(provider => {
    const lowerCode = provider.code.toLowerCase();
    
    // Si este código ya existe, decidir cuál mantener
    if (processedCodes[lowerCode]) {
      const existingProvider = processedCodes[lowerCode];
      
      // Si el existente está inactivo y el actual activo, reemplazar
      if (existingProvider.status === 0 && provider.status === 1) {
        // Encontrar el índice del proveedor existente
        const index = result.findIndex(p => p.id === existingProvider.id);
        if (index !== -1) {
          // Reemplazar con el proveedor activo
          result[index] = provider;
          processedCodes[lowerCode] = provider;
        }
      }
      // Si ambos están activos o el existente está activo y el actual inactivo,
      // mantener el existente (no hacer nada)
    } else {
      // Es la primera vez que vemos este código, simplemente agregarlo
      result.push(provider);
      processedCodes[lowerCode] = provider;
    }
  });
  
  return result;
};

// Filtrar proveedores según la categoría y agrupar duplicados
const filteredProviders = computed(() => {
  // Si no hay categoría especificada, retorna todos los proveedores
  let providers = [];
  if (!props.category) {
    providers = [...providersStore.providers];
  } else {
    // Filtrar proveedores que tienen juegos en la categoría especificada
    providers = providersStore.providers.filter(provider => {
      // Verificar si este proveedor tiene juegos en la categoría especificada
      const providerGames = gamesStore.games.filter(game => 
        game.provider_code === provider.code
      );
      
      return providerGames.some(game => 
        game.categories && game.categories.includes(props.category)
      );
    });
  }
  
  // Agrupar proveedores duplicados
  return groupDuplicateProviders(providers);
});

// Comprobar si debemos mostrar en modo filas
const showAsRow = computed(() => {
  // Si es móvil, nunca mostrar filas
  if (isMobile.value) {
    return false;
  }
  
  // Si no es móvil, mostrar filas si displayMode es 'rows'
  return props.displayMode === 'rows';
});



// Methods
const handleProviderChange = (event) => {
  if (event.target.value === 'all') {
    providersStore.setSelectedProvider(null);
  } else {
    const provider = filteredProviders.value.find(p => p.code === event.target.value);
    providersStore.setSelectedProvider(provider);
  }
  emit('page-reset');
};

const selectProvider = (provider) => {
  providersStore.setSelectedProvider(provider);
  // Emit el cambio de página para que el padre pueda resetear la paginación
  emit('page-reset');
};

// Definir emit
const emit = defineEmits(['page-reset']);
</script>

<template>
  <!-- Dropdown view - NO TOCAR -->
 <!-- Reemplazar el dropdown por este componente personalizado -->
<div 
  v-if="!showAsRow"
  class="custom-dropdown provider-select"
>
  <div 
    class="selected-display"
    @click="isDropdownOpen = !isDropdownOpen"
  >
    <img 
      v-if="selectedProvider?.icon" 
      :src="getCorrectImageUrl(selectedProvider.icon)" 
      class="provider-icon" 
      alt=""
    />
    <span>{{ selectedProvider?.name || t('app.provider') }}</span>
    <span class="dropdown-arrow">▼</span>
  </div>
  
  <div class="dropdown-options" v-show="isDropdownOpen">
    <div 
      v-if="showAllOption" 
      class="dropdown-option"
      :class="{ active: !selectedProvider }"
      @click="handleCustomDropdownChange('all')"
    >
      {{ t('app.provider') }}
    </div>
    
    <div 
      v-for="provider in filteredProviders"
      :key="provider.id"
      class="dropdown-option"
      :class="{ active: selectedProvider?.code === provider.code }"
      @click="handleCustomDropdownChange(provider.code)"
    >
      <img 
        v-if="provider.icon" 
        :src="getCorrectImageUrl(provider.icon)" 
        class="provider-icon" 
        alt=""
      />
      <span>{{ provider.name }}</span>
    </div>
  </div>
</div>
  <!-- Scroll horizontal para desktop -->
  <div v-else class="providers-scroll-wrapper">
  <button class="scroll-btn left" @click="scrollLeft">‹</button>

  <div ref="scrollContainer" class="providers-scroll-row">
    <div 
      v-for="provider in filteredProviders" 
      :key="provider.id" 
      class="provider-item"
      :class="{ 'selected': selectedProvider?.code === provider.code }"
      @click="selectProvider(provider)"
    >
      <img 
        v-if="provider.cover" 
        :src="getCorrectImageUrl(provider.cover)" 
        :alt="provider.name" 
        class="provider-logo"
      />
      <img 
        v-else-if="provider.icon" 
        :src="getCorrectImageUrl(provider.icon)" 
        :alt="provider.name" 
        class="provider-logo"
      />
      <span v-else class="provider-name">{{ provider.name }}</span>
    </div>
  </div>

  <button class="scroll-btn right" @click="scrollRight">›</button>
</div>


</template>

<style scoped>
@import '../assets/styles/main.css';

/* Estilos para la vista en filas */
.providers-row-container {
  width: 100%;
  margin-bottom: 1rem;
}

.providers-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.provider-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.5rem;
  margin-right: 0.5rem;
 
  border: 2px solid #d0d404e3;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #0f012f;
  color: white;
}

.provider-item:hover {
  background-color: #23dc12;
}

.provider-item.selected {
  background-color: #1a3c4a;
  border-color: #0096c7;
}

.provider-logo {
  height: 22px;
  margin-right: 0.5rem;
}
/* Estilos para el dropdown personalizado manteniendo la clase provider-select */
.custom-dropdown {
  position: relative;
  display: inline-block;
  text-align: center;
  cursor: pointer;
}

.selected-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  width: 100%;
}

.dropdown-arrow {
  font-size: 10px;
  margin-left: 4px;
}

.dropdown-options {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  background: #000110;
  border: 2px solid #0122fd;
  border-top: none;
  border-radius: 0 0 8px 8px;
  z-index: 100;
  text-align: left;
}

.dropdown-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
}

.dropdown-option:hover {
  background-color: rgba(1, 34, 253, 0.3);
}

.dropdown-option.active {
  background-color: rgba(1, 34, 253, 0.5);
}

.provider-icon {
  height: 16px;
  width: auto;
  margin-right: 8px;
  object-fit: contain;
}
.providers-scroll-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 2.5rem; /* Espacio para flechas */
  box-sizing: border-box;
}

.providers-scroll-row {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  gap: 0.6rem;
  width: 100%;
  justify-content: flex-start;
}

.providers-scroll-row.no-scroll {
  justify-content: center;
}

.scroll-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: #000;
  border: 2px solid #00ffea;
  color: #00ffea;
  font-size: 2rem;
  cursor: pointer;
  z-index: 10;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  line-height: 1;
  text-align: center;
  transition: all 0.3s ease-in-out;
  box-shadow: 0 0 12px #00ffeab0;
}

.scroll-btn:hover {
  background-color: #00ffea;
  color: #000;
  box-shadow: 0 0 18px #00ffea, 0 0 24px #00ffea;
}

.scroll-btn.left {
  left: 8px;
}

.scroll-btn.right {
  right: 8px;
}

/* 🔒 Mobile usa dropdown, oculta flechas y scroll row */
@media (max-width: 768px) {
  .providers-scroll-wrapper {
    display: none;
  }

  .scroll-btn {
    display: none;
  }
}


</style>